import {
  type AssetVizHoverEvent,
  type AssetVizProps,
  type AssetVizSceneConfigOptions,
} from '@asset-viz/defines'
import {
  useCdfBinReading,
  useColorScale,
  useQuantitativeDataSummaryStats,
} from 'features/dataLayers'
import * as React from 'react'
import {
  type GeckoColorScaleThreshold,
  GeckoComponentCoords,
  type GeckoComponentDataFrame,
  type GeckoMeasurementRange,
} from 'types'

import { useCdfDisplayOverride } from './useCdfDisplayOverride'
import { useDefaultColorThreshold } from './useDefaultColorThreshold'

const DEFAULT_SCENE_OPTIONS: AssetVizSceneConfigOptions = {
  threeDimensionsEnabled: true,
  featuresEnabled: true,
}

export type ForecastAssetVizWindowState = ReturnType<
  typeof useForecastAssetVizWindowState
>

export function useForecastAssetVizWindowState({
  selectedCDF,
  initialShow3D,
  onHovered: externalOnHovered,
  getColorThresholdForComponent: externalGetColorThreshold,
  cdfToFilteredValueRange: externalCdfToFilteredValueRange,
  onSceneConfigOptionsChanged: externalOnSceneConfigOptionsChanged,
  selectedColorThreshold: controlledSelectedColorThreshold,
  onSelectedColorThresholdChange,
  filteredValueRange: controlledFilteredValueRange,
  onFilteredValueRangeChange,
}: {
  selectedCDF: GeckoComponentDataFrame | undefined
  initialShow3D: boolean
  onHovered: AssetVizProps['onHovered']
  getColorThresholdForComponent: AssetVizProps['getColorThresholdForComponent']
  cdfToFilteredValueRange: AssetVizProps['cdfToFilteredValueRange']
  onSceneConfigOptionsChanged: AssetVizProps['onSceneConfigOptionsChanged']
  selectedColorThreshold: GeckoColorScaleThreshold | undefined
  onSelectedColorThresholdChange:
    | ((threshold: GeckoColorScaleThreshold | undefined) => void)
    | undefined
  filteredValueRange: GeckoMeasurementRange | undefined
  onFilteredValueRangeChange:
    | ((range: GeckoMeasurementRange | undefined) => void)
    | undefined
}) {
  const [show3D, setShow3D] = React.useState(initialShow3D)
  const [sceneOptions, setSceneOptions] =
    React.useState<AssetVizSceneConfigOptions>(DEFAULT_SCENE_OPTIONS)
  const [hoveredCoords, setHoveredCoords] = React.useState<
    GeckoComponentCoords | undefined
  >()
  const stats = useQuantitativeDataSummaryStats(selectedCDF)
  const defaultColorThreshold = useDefaultColorThreshold(selectedCDF)

  const [selectedColorThreshold, setSelectedColorThreshold] =
    useCdfDisplayOverride({
      selectedCDF,
      controlledValue: controlledSelectedColorThreshold,
      onControlledChange: onSelectedColorThresholdChange,
      fallback: defaultColorThreshold,
    })
  const [filteredValueRange, setFilteredValueRange] = useCdfDisplayOverride({
    selectedCDF,
    controlledValue: controlledFilteredValueRange,
    onControlledChange: onFilteredValueRangeChange,
  })

  const { colorScale: selectedColorScale } = useColorScale(
    selectedCDF,
    selectedColorThreshold
  )
  const { value: cursorValue } = useCdfBinReading(selectedCDF, hoveredCoords)

  React.useEffect(() => {
    setShow3D(initialShow3D)
  }, [initialShow3D, selectedCDF?.tags.component])

  React.useEffect(() => {
    if (!sceneOptions.threeDimensionsEnabled) {
      setShow3D(false)
    }
  }, [sceneOptions.threeDimensionsEnabled])

  const effectiveShow3D = show3D && sceneOptions.threeDimensionsEnabled
  const lo = selectedColorThreshold?.min ?? stats.data?.value_range?.min
  const hi = selectedColorThreshold?.max ?? stats.data?.value_range?.max
  const loColor = React.useMemo(() => {
    if (!lo) return undefined
    return selectedColorScale.getColorForValue(lo.num)
  }, [lo, selectedColorScale])
  const hiColor = React.useMemo(() => {
    if (!hi) return undefined
    return selectedColorScale.getColorForValue(hi.num)
  }, [hi, selectedColorScale])
  const cursorColor = React.useMemo(() => {
    if (!cursorValue) return undefined
    return selectedColorScale.getColorForValue(cursorValue.num)
  }, [cursorValue, selectedColorScale])

  const onHovered = React.useCallback(
    (tooltipFrozen: boolean, event: AssetVizHoverEvent | undefined) => {
      externalOnHovered?.(tooltipFrozen, event)
      if (tooltipFrozen) return

      setHoveredCoords((current) => {
        if (GeckoComponentCoords.Equals(current, event?.coords)) return current
        return event?.coords
      })
    },
    [externalOnHovered]
  )

  const getColorThresholdForComponent = React.useCallback(
    (cdf: GeckoComponentDataFrame) => {
      if (cdf.id === selectedCDF?.id) return selectedColorThreshold
      return externalGetColorThreshold?.(cdf)
    },
    [externalGetColorThreshold, selectedCDF?.id, selectedColorThreshold]
  )

  const cdfToFilteredValueRange = React.useCallback(
    (cdf: GeckoComponentDataFrame) => {
      if (cdf.id === selectedCDF?.id) return filteredValueRange
      return externalCdfToFilteredValueRange?.(cdf)
    },
    [externalCdfToFilteredValueRange, filteredValueRange, selectedCDF?.id]
  )

  const onSceneConfigOptionsChanged = React.useCallback(
    (options: React.SetStateAction<AssetVizSceneConfigOptions>) => {
      setSceneOptions(options)
      externalOnSceneConfigOptionsChanged?.(options)
    },
    [externalOnSceneConfigOptionsChanged]
  )

  const hasRangeControls =
    !!selectedCDF &&
    !!selectedColorThreshold &&
    !!stats.data?.value_range &&
    !!stats.data.histogram

  return {
    selectedCDF,
    selectedColorScale,
    statsData: stats.data,
    selectedColorThreshold,
    setSelectedColorThreshold,
    filteredValueRange,
    setFilteredValueRange,
    hoveredCoords,
    cursorValue,
    cursorColor,
    lo,
    hi,
    loColor,
    hiColor,
    setShow3D,
    effectiveShow3D,
    threeDimensionsEnabled: sceneOptions.threeDimensionsEnabled,
    hasRangeControls,
    onHovered,
    getColorThresholdForComponent,
    cdfToFilteredValueRange,
    onSceneConfigOptionsChanged,
  }
}
